import { useEffect, useRef } from 'react'
import { captureEvent } from '@/lib/analytics'

// Fires `app_active_heartbeat` every TICK_MS while the tab is visible AND the
// user has had input within IDLE_MS. Summed over a day, count × TICK_MS gives
// approximate active time per user — distinct from raw "tab open" time.

const TICK_MS = 60_000        // emit at most once per minute
const IDLE_MS = 5 * 60_000    // 5 minutes of no input = idle, stop emitting
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'keydown',
  'scroll',
  'click',
  'touchstart',
]

export function useActivityHeartbeat(enabled: boolean): void {
  const lastActivityRef = useRef<number>(Date.now())

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const markActive = () => { lastActivityRef.current = Date.now() }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') markActive()
    }

    ACTIVITY_EVENTS.forEach(ev => {
      document.addEventListener(ev, markActive, { passive: true })
    })
    document.addEventListener('visibilitychange', onVisibilityChange)

    const tick = () => {
      const idleFor = Date.now() - lastActivityRef.current
      if (document.visibilityState !== 'visible' || idleFor > IDLE_MS) return
      captureEvent('app_active_heartbeat', {
        path: window.location.pathname,
        tick_ms: TICK_MS,
        idle_ms: idleFor,
      })
    }

    const intervalId = window.setInterval(tick, TICK_MS)

    return () => {
      window.clearInterval(intervalId)
      ACTIVITY_EVENTS.forEach(ev => {
        document.removeEventListener(ev, markActive)
      })
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enabled])
}
